import { GenericAbortSignal } from 'axios';

import { useInfiniteQuery } from '@tanstack/react-query';

import {
  DischargeChecklist,
  DischargeChecklistHistoryEntry,
  DischargeChecklistItem,
} from 'models/DischargeChecklistItem';
import { Paginated } from 'models/Paginated';
import ProjectedDischarge, { ProjectedDischargeOptions } from 'models/reviews/ProjectedDischarge';
import http from 'services/http';

export const dischargeChecklistQueryKeys = {
  base: ['dischargeChecklists'],
  index: (clientId?: string) => ['dischargeChecklists', 'index', { clientId }],
  // A patient's item history. Its own key space, not ['activities']: invalidating that would refetch the
  // whole patient story feed.
  historyBase: ['dischargeChecklists', 'history'],
  history: (reviewId: string, itemId: string) => ['dischargeChecklists', 'history', { reviewId, itemId }],
};

// Undefined clientId is the acting client's own lists (Configuration). A clientId is an Olio admin
// looking at that client's lists from Admin > Clients.
function basePath(clientId?: string) {
  return clientId ? `/admin/clients/${clientId}` : '';
}

export async function indexDischargeChecklists(clientId: string | undefined, signal?: GenericAbortSignal) {
  return http
    .get<{ data: DischargeChecklist[] }>(`${basePath(clientId)}/discharge_checklists`, { signal })
    .then((response) => response.data.data);
}

export async function createDischargeChecklistItem({
  clientId,
  ...params
}: {
  clientId?: string;
  groupTypeId: string;
  label: string;
}) {
  return http
    .post<DischargeChecklistItem>(`${basePath(clientId)}/discharge_checklist_items`, params)
    .then((response) => response.data);
}

export async function updateDischargeChecklistItem({
  id,
  clientId,
  label,
}: {
  id: string;
  clientId?: string;
  label: string;
}) {
  return http
    .patch<DischargeChecklistItem>(`${basePath(clientId)}/discharge_checklist_items/${id}`, { label })
    .then((response) => response.data);
}

export async function destroyDischargeChecklistItem({ id, clientId }: { id: string; clientId?: string }) {
  return http.delete(`${basePath(clientId)}/discharge_checklist_items/${id}`);
}

// Every item in the list with its new 0-based position. The server stores what it's handed.
export async function reorderDischargeChecklistItems({
  clientId,
  groupTypeId,
  items,
}: {
  clientId?: string;
  groupTypeId: string;
  items: { id: string; position: number }[];
}) {
  return http
    .patch<{
      data: DischargeChecklistItem[];
    }>(`${basePath(clientId)}/discharge_checklist_items`, { groupTypeId, items })
    .then((response) => response.data.data);
}

export enum DischargeChecklistOperation {
  CHECK = 'check',
  UNCHECK = 'uncheck',
  MARK_NOT_NEEDED = 'mark_not_needed',
  MARK_NEEDED = 'mark_needed',
  SET_NOTE = 'set_note',
}

// One item on a patient's checklist (their projected discharge review), not the client's configuration.
// The server answers with the review and its checklist.
export async function updatePatientDischargeChecklistItem({
  reviewId,
  itemId,
  operation,
  noteText,
}: {
  reviewId: string;
  itemId: string;
  operation: DischargeChecklistOperation;
  noteText?: string;
}) {
  return http
    .patch<ProjectedDischargeOptions>(`/reviews/${reviewId}/discharge_checklist_items/${itemId}`, {
      operation,
      noteText,
    })
    .then((response) => new ProjectedDischarge(response.data));
}

export async function indexDischargeChecklistItemHistory(
  { reviewId, itemId, page }: { reviewId: string; itemId: string; page: number },
  signal?: GenericAbortSignal
) {
  return http
    .get<
      Paginated<DischargeChecklistHistoryEntry>
    >(`/reviews/${reviewId}/discharge_checklist_items/${itemId}/history`, { params: { page }, signal })
    .then((response) => response.data);
}

// Every change to one item, newest first, a page at a time as the history window scrolls.
export const useDischargeChecklistItemHistory = (reviewId: string, itemId: string) =>
  useInfiniteQuery({
    queryKey: dischargeChecklistQueryKeys.history(reviewId, itemId),
    queryFn: ({ pageParam, signal }) =>
      indexDischargeChecklistItemHistory({ reviewId, itemId, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined,
    refetchOnMount: 'always',
  });
